import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyMessageBar,
  SkyText,
  type SkyBadgeColor,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import type { CSSProperties } from "react";
import {
  VERIFY_REASON_SENTENCES,
  type IntegrityChainView,
  type IntegrityVerification,
} from "@shared/types/auditIntegrity";
import { ErrorState, LoadingRows } from "@/components/access/ScreenStates";
import { StatusCell } from "@/components/runs/StatusCell";
import {
  useInstanceIntegrity,
  usePlatformIntegrity,
  useVerifyNow,
} from "@/hooks/use-audit-integrity";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { LIST_GRID_PAGINATION, pixelColumnLayout } from "@/lib/grid";
import {
  formatCalendarDay,
  formatCalendarMonth,
  formatInstant,
  instantHoverTitle,
} from "@/lib/time/displayTime";
import { ZoneCaption } from "@/components/shell/ZoneCaption";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * The audit trail's integrity (docs/research/2026-09-28-design-hipaa-audit.md, "UI"): the
 * Platform > Audit trail "Integrity" tab (one row per chain and "Verify now") and the one line
 * under an instance's trail. Every status is a colour and a word; every time is in the reader's display zone. Nothing here
 * shows a row, a hash or a storage URL: the runner's result, as counts, times and a fixed
 * sentence per reason.
 */

type Presented = { text: string; color: SkyBadgeColor };

/** "Intact", "Broken", "Not run", or "Not checked" when the check could not run. */
export function integrityStatus(verification: IntegrityVerification | null): Presented {
  switch (verification?.status) {
    case "ok":
      return { text: "Intact", color: "success" };
    case "broken":
      return { text: "Broken", color: "danger" };
    case "error":
      return { text: "Not checked", color: "warning" };
    case "running":
      return { text: "Checking", color: "informative" };
    default:
      return { text: "Not run", color: "subtle" };
  }
}

/**
 * Fixes wave 4b (post-release check 4, 02a-integrity-before.png, 1440 px): every column was a
 * percentage, so "Last verified", "Rows checked" and "Status" sat with almost no gap. A
 * header's width is a fact of the font: every column but Chain has a border-box pixel width its
 * header needs (measured in Chromium, the larger of the app's stack and Inter, 16 px semibold,
 * plus 32 px padding: 145, 159, 91, 135 and 190 px) and its value fits in (a display-zone time,
 * round 3, is at most 147 px at 12 px, "May 28, 2026, 08:08 AM"; "Not checked" is a 90 px badge). Chain takes the rest, down to 136 px (a
 * long label truncates, whole in its title); the floor, 960 px, fits the pane at 1280 px.
 */
const INTEGRITY_WIDTHS = {
  lastVerification: 208,
  rowsChecked: 168,
  status: 120,
  lastExportDay: 136,
  oldestHotMonth: 192,
} as const;
const INTEGRITY_MINIMUMS: Readonly<Record<string, number>> = { label: 136 };

/**
 * When the daily check starts, in the reader's display zone and named: "07:45 AM IST". The runner
 * starts it at 02:15 UTC every day (`Date.UTC` below is that fixed start, arithmetic only); #1078
 * (owner, 2026-10-15) removed the UTC time that stood beside it.
 */
export function dailyCheckTime(zone: string, now: Date = new Date()): string {
  const at = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 2, 15));
  return formatInstant(at, zone, "time", { named: true });
}

function reasonSentence(verification: IntegrityVerification): string {
  return verification.reason ? VERIFY_REASON_SENTENCES[verification.reason] : "";
}

export function AuditIntegrityPanel() {
  const zone = useDisplayTz();
  const integrity = usePlatformIntegrity(true);
  const verifyNow = useVerifyNow();
  const verifyError = useInlineError();
  const notice = useNotice();

  if (integrity.isPending) return <LoadingRows rows={4} columns={6} />;
  if (integrity.isError) {
    return (
      <ErrorState
        title="The integrity results did not load"
        error={integrity.error}
        onRetry={() => void integrity.refetch()}
      />
    );
  }
  const chains = integrity.data.chains;
  const broken = chains.filter((chain) => chain.lastVerification?.status === "broken");

  function handleVerify() {
    verifyError.clear();
    verifyNow.mutate(undefined, {
      onSuccess: (view) => notice.success(view.message, { title: "Verification requested" }),
      onError: (error) => verifyError.show(error, "The verification could not be requested."),
    });
  }

  const columns: SkyDataGridColumn<IntegrityChainView>[] = [
    {
      key: "label",
      header: "Chain",
      width: "100%",
      sortable: false,
      renderCell: (_value, row) => (
        <SkyText size={200} block truncate title={row.label}>
          {row.label}
        </SkyText>
      ),
    },
    {
      key: "lastVerification",
      header: "Last verified",
      width: `${INTEGRITY_WIDTHS.lastVerification}px`,
      sortable: false,
      renderCell: (_value, row) => (
        <SkyText
          size={200}
          font="numeric"
          title={instantHoverTitle(row.lastVerification?.finishedAt, zone)}
        >
          {row.lastVerification?.finishedAt
            ? formatInstant(row.lastVerification.finishedAt, zone, "minutes")
            : "Never"}
        </SkyText>
      ),
    },
    {
      key: "rowsChecked",
      header: "Rows checked",
      width: `${INTEGRITY_WIDTHS.rowsChecked}px`,
      sortable: false,
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {row.lastVerification ? row.lastVerification.rowsChecked.toLocaleString("en-US") : "None"}
        </SkyText>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: `${INTEGRITY_WIDTHS.status}px`,
      sortable: false,
      renderCell: (_value, row) => {
        const status = integrityStatus(row.lastVerification);
        return (
          <StatusCell
            testId={`integrity-status-${row.chain}`}
            badge={
              <SkyBadge appearance="tint" color={status.color}>
                {status.text}
              </SkyBadge>
            }
          />
        );
      },
    },
    {
      key: "lastExportDay",
      header: "Last export",
      width: `${INTEGRITY_WIDTHS.lastExportDay}px`,
      sortable: false,
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {row.lastExportDay ? formatCalendarDay(row.lastExportDay) : "None yet"}
        </SkyText>
      ),
    },
    {
      key: "oldestHotMonth",
      header: "Oldest hot month",
      width: `${INTEGRITY_WIDTHS.oldestHotMonth}px`,
      sortable: false,
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {row.oldestHotMonth ? formatCalendarMonth(row.oldestHotMonth) : "None"}
        </SkyText>
      ),
    },
  ];

  const layout = pixelColumnLayout(columns, { minimums: INTEGRITY_MINIMUMS, selection: false });

  return (
    <div className="flex flex-col gap-4" data-testid="audit-integrity">
      <div className="flex flex-col gap-2">
        <SkyText size={200} color="muted" block>
          {`Every audit trail is checked daily from ${dailyCheckTime(zone)}: each row must still match the hash it was written with and link to the row before it, and the newest rows must match the write-once export.`}
        </SkyText>
        <div className="flex flex-wrap items-center gap-2">
          <SkyButton
            appearance="secondary"
            testId="audit-verify-now"
            loading={verifyNow.isPending}
            onClick={handleVerify}
          >
            Verify now
          </SkyButton>
          {integrity.data.pendingRequestAt ? (
            <SkyText size={200} color="muted" testId="audit-verify-pending">
              {`Verification requested at ${formatInstant(integrity.data.pendingRequestAt, zone, "short", { named: true })}. Results appear here within a few minutes.`}
            </SkyText>
          ) : null}
        </div>
        {verifyError.error ? (
          <InlineErrorBar
            error={verifyError.error}
            title="The verification could not be requested"
            onDismiss={verifyError.clear}
            testId="audit-verify-error"
          />
        ) : null}
      </div>
      {broken.map((chain) => {
        const verification = chain.lastVerification as IntegrityVerification;
        return (
          <SkyMessageBar
            key={chain.chain}
            intent="error"
            layout="multiline"
            testId={`integrity-broken-${chain.chain}`}
            title={`${chain.label} failed its integrity check`}
            message={`${reasonSentence(verification)}${
              verification.firstBadSeq
                ? ` The first bad row is sequence ${verification.firstBadSeq}.`
                : ""
            } Do not repair rows: follow the incident procedure. Skypoint has been alerted.`}
          />
        );
      })}
      <div style={{ "--audit-integrity-floor": `${layout.floor}px` } as CSSProperties}>
        <SkyDataGrid<IntegrityChainView>
          data={chains}
          columns={layout.columns}
          getRowId={(row) => row.chain}
          testId="audit-integrity-grid"
          className="[&_table]:min-w-(--audit-integrity-floor)"
          ariaLabel="Audit trail integrity by chain"
          emptyMessage="No audit trail yet."
          {...LIST_GRID_PAGINATION}
        />
        <ZoneCaption testId="audit-integrity-zone-caption" className="mt-2" />
      </div>
    </div>
  );
}

/** The one line under an instance's trail: when it was verified, or that it failed. */
export function InstanceIntegrityLine({ enabled }: { enabled: boolean }) {
  const zone = useDisplayTz();
  const integrity = useInstanceIntegrity(enabled);
  const data = integrity.data;
  if (!enabled || !data?.enabled) return null;
  const verification = data.lastVerification;
  if (verification?.status === "broken") {
    return (
      <SkyMessageBar
        intent="error"
        layout="multiline"
        testId="instance-integrity-broken"
        title="Integrity check failed"
        message={`This audit trail failed its integrity check on ${formatInstant(
          verification.finishedAt ?? verification.startedAt,
          zone,
          "short",
          { named: true },
        )}. Skypoint has been alerted.`}
      />
    );
  }
  const text =
    verification?.status === "ok" && verification.finishedAt
      ? `Integrity verified ${formatInstant(verification.finishedAt, zone, "short", { named: true })}`
      : `Integrity not verified yet. The daily check runs from ${dailyCheckTime(zone)}.`;
  return (
    <SkyText size={200} color="muted" block testId="instance-integrity-line">
      {text}
    </SkyText>
  );
}
